// client/src/components/pages/LexiStar/DispatchBoard/boardLogic.ts (ST-00250)
//
// Pure helpers of the dispatch board: the absorption maths (a mirror of
// server/modules/lexistar/dispatchboard/absorption.ts for the optimistic display while a request
// is in flight — keep the two in step), the words and colours of an absorption state, SAST day
// helpers, the drag payload, and the order a planner sees trucks in.

import type {
  Absorption,
  AbsorptionState,
  BoardAllocation,
  BoardOrder,
  BoardTruck,
} from '../../../../types/lexistar-dispatch-board';

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function stateOf(allocated: number, target: number, tolerance: number): AbsorptionState {
  if (allocated <= 0) return 'none';
  if (allocated < target - tolerance) return 'under';
  if (allocated > target + tolerance) return 'over';
  return 'on';
}

/** absorptionOf as the server computes it (absorption.ts). */
export function absorptionOf(
  order: Pick<BoardOrder, 'target_tonnes' | 'target_loads' | 'tonnes_per_load'>,
  allocations: ReadonlyArray<Pick<BoardAllocation, 'planned_tonnes' | 'counts'>>
): Absorption {
  const counting = allocations.filter((a) => a.counts);
  const estimated = counting.filter((a) => a.planned_tonnes != null && Number.isFinite(Number(a.planned_tonnes)));
  const allocated_loads = counting.length;
  const allocated_tonnes = round2(estimated.reduce((s, a) => s + Number(a.planned_tonnes), 0));
  const base = { allocated_loads, allocated_tonnes, unestimated_loads: counting.length - estimated.length };
  const targetTonnes = order.target_tonnes != null ? Number(order.target_tonnes) : null;
  const targetLoads = order.target_loads != null ? Number(order.target_loads) : null;
  if (targetTonnes != null && targetTonnes > 0) {
    const perLoad =
      order.tonnes_per_load != null && Number(order.tonnes_per_load) > 0
        ? Number(order.tonnes_per_load)
        : estimated.length
          ? allocated_tonnes / estimated.length
          : 0;
    const tolerance = round2(perLoad / 2);
    const ratio = allocated_tonnes / targetTonnes;
    return {
      ...base,
      target_kind: 'tonnes',
      target: targetTonnes,
      ratio: round2(ratio),
      pct: Math.round(ratio * 100),
      state: stateOf(allocated_tonnes, targetTonnes, tolerance),
      remaining: round2(targetTonnes - allocated_tonnes),
      tolerance,
    };
  }
  if (targetLoads != null && targetLoads > 0) {
    const ratio = allocated_loads / targetLoads;
    return {
      ...base,
      target_kind: 'loads',
      target: targetLoads,
      ratio: round2(ratio),
      pct: Math.round(ratio * 100),
      state: stateOf(allocated_loads, targetLoads, 0),
      remaining: targetLoads - allocated_loads,
      tolerance: 0,
    };
  }
  return { ...base, target_kind: null, target: null, ratio: null, pct: null, state: 'none', remaining: null, tolerance: null };
}

// ── Words and colours ─────────────────────────────────────────────────────────────────

export interface StateMeta {
  /** The word on the ring and the pill (never colour alone). */
  word: string;
  /** A glyph that survives greyscale: ◔ under, ● on, ◉ over, ○ none. */
  glyph: string;
  /** Tailwind classes over the theme tokens (both themes). */
  ring: string;
  text: string;
  pill: string;
  panel: string;
}

/**
 * Colour-blind safe: blue for under (room left), green for on, orange for over — three hues that
 * stay distinct under deuteranopia / protanopia — each paired with a word and a glyph. The Tailwind
 * palette utilities flip with the theme (index.css custom properties).
 */
export const STATE_META: Record<AbsorptionState, StateMeta> = {
  none: {
    word: 'Nothing allocated',
    glyph: '○',
    ring: 'stroke-slate-300',
    text: 'text-slate-500',
    pill: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200',
    panel: 'border-slate-200',
  },
  under: {
    word: 'Under target',
    glyph: '◔',
    ring: 'stroke-sky-600',
    text: 'text-sky-800',
    pill: 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-300',
    panel: 'border-sky-300',
  },
  on: {
    word: 'On target',
    glyph: '●',
    ring: 'stroke-emerald-600',
    text: 'text-emerald-800',
    pill: 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-300',
    panel: 'border-emerald-400',
  },
  over: {
    word: 'Over target',
    glyph: '◉',
    ring: 'stroke-orange-600',
    text: 'text-orange-800',
    pill: 'bg-orange-50 text-orange-800 ring-1 ring-inset ring-orange-300',
    panel: 'border-orange-400',
  },
};

export const fmtTonnes = (t: number | null | undefined, digits = 0): string =>
  t == null || !Number.isFinite(Number(t)) ? '—' : `${Number(t).toLocaleString('en-GB', { maximumFractionDigits: digits })} t`;

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** "340 of 600 t · 10 loads" / "4 of 5 loads". */
export function absorptionText(a: Absorption): string {
  if (a.target_kind === 'tonnes') {
    const loads = plural(a.allocated_loads, 'load');
    const un = a.unestimated_loads ? ` (${a.unestimated_loads} unestimated)` : '';
    return `${fmtTonnes(a.allocated_tonnes)} of ${fmtTonnes(a.target)} · ${loads}${un}`;
  }
  if (a.target_kind === 'loads') return `${a.allocated_loads} of ${plural(a.target ?? 0, 'load')}${a.allocated_tonnes ? ` · ${fmtTonnes(a.allocated_tonnes)}` : ''}`;
  return plural(a.allocated_loads, 'load');
}

/** "260 t to go" / "80 t over" / "2 loads to go" / "Done". */
export function remainingText(a: Absorption): string | null {
  if (a.remaining == null || a.target_kind == null) return null;
  const unit = a.target_kind === 'tonnes' ? (n: number) => fmtTonnes(n) : (n: number) => plural(n, 'load');
  if (a.state === 'on') return 'On target';
  if (a.remaining > 0) return `${unit(a.remaining)} to go`;
  if (a.remaining < 0) return `${unit(-a.remaining)} over`;
  return 'On target';
}

// ── Days (SAST) ───────────────────────────────────────────────────────────────────────

export const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const isDay = (s: string | null | undefined): s is string => !!s && DAY_RE.test(s);

/** The SAST calendar day of an instant. */
export function sastDay(at: Date | number = Date.now()): string {
  const ms = typeof at === 'number' ? at : at.getTime();
  return new Date(ms + 2 * 3600000).toISOString().slice(0, 10);
}
export const todayString = (): string => sastDay();
/** A Date for the pickers: local noon of the day (no DST edge in SAST, but noon is safe anywhere). */
export const parseDay = (day: string): Date => new Date(`${day}T12:00:00`);
export const dayString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export function shiftDay(day: string, n: number): string {
  const d = parseDay(day);
  d.setDate(d.getDate() + n);
  return dayString(d);
}
export function dayLabel(day: string): string {
  if (day === todayString()) return 'Today';
  if (day === shiftDay(todayString(), 1)) return 'Tomorrow';
  if (day === shiftDay(todayString(), -1)) return 'Yesterday';
  return parseDay(day).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "06:00" of an ISO instant, SAST. */
export const fmtTime = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Africa/Johannesburg', hour: '2-digit', minute: '2-digit' }) : '—';

// ── Drag and drop ─────────────────────────────────────────────────────────────────────

/** The one MIME type the board drags: a truck from the rail or an allocation from a card. */
export const DRAG_TYPE = 'application/x-lexibridge-dispatch';

export type DragPayload = { kind: 'truck'; asset_id: string } | { kind: 'allocation'; allocation_id: string; order_id: string; asset_id: string };

export function encodeDrag(p: DragPayload): string {
  return JSON.stringify(p);
}

export function decodeDrag(text: string | null | undefined): DragPayload | null {
  if (!text) return null;
  try {
    const p = JSON.parse(text) as Partial<DragPayload> & { kind?: string };
    if (p.kind === 'truck' && typeof (p as { asset_id?: unknown }).asset_id === 'string') return p as DragPayload;
    if (p.kind === 'allocation' && typeof (p as { allocation_id?: unknown }).allocation_id === 'string') return p as DragPayload;
    return null;
  } catch {
    return null;
  }
}

// ── Trucks ────────────────────────────────────────────────────────────────────────────

export const truckName = (t: Pick<BoardTruck, 'registration' | 'fleet_no'> | Pick<BoardAllocation, 'registration' | 'fleet_no'>): string =>
  t.registration || t.fleet_no || 'Unregistered';

/** The rail: available trucks first (fewest loads on the board first, then by name), then the unavailable. */
export function sortTrucks(trucks: readonly BoardTruck[]): BoardTruck[] {
  return [...trucks].sort((a, b) => {
    const ua = a.availability === 'available' ? 0 : 1;
    const ub = b.availability === 'available' ? 0 : 1;
    if (ua !== ub) return ua - ub;
    if (a.loads_on_board !== b.loads_on_board) return a.loads_on_board - b.loads_on_board;
    return truckName(a).localeCompare(truckName(b));
  });
}

/** A short line under the truck: its trip, its workshop state, or its loads today. */
export function truckStatusText(t: BoardTruck): string {
  if (t.availability === 'breakdown') return `Broken down${t.unavailable_label ? `: ${t.unavailable_label}` : ''}${t.unavailable_until ? ` · until ${fmtTime(t.unavailable_until)}` : ''}`;
  if (t.availability === 'planned_maintenance') return `In the workshop${t.unavailable_label ? `: ${t.unavailable_label}` : ''}${t.unavailable_until ? ` · until ${fmtTime(t.unavailable_until)}` : ''}`;
  if (t.trip && t.trip.stage !== 'PLANNED') return `${t.trip.reference || 'Trip'} · ${t.trip.stage.toLowerCase().replace(/_/g, ' ')}`;
  if (t.loads_on_board) return `${plural(t.loads_on_board, 'load')} on this board${t.tonnes_on_board ? ` · ${fmtTonnes(t.tonnes_on_board)}` : ''}`;
  if (t.trip) return `${t.trip.reference || 'Trip'} planned ${fmtTime(t.trip.planned_start)}`;
  return 'Free';
}

/** The counting allocations of an order grouped by truck: [{ asset_id, name, allocations }]. */
export function allocationsByTruck(order: BoardOrder): Array<{ asset_id: string; name: string; allocations: BoardAllocation[] }> {
  const map = new Map<string, { asset_id: string; name: string; allocations: BoardAllocation[] }>();
  for (const a of order.allocations) {
    if (!a.counts) continue;
    const g = map.get(a.asset_id) ?? { asset_id: a.asset_id, name: truckName(a), allocations: [] };
    g.allocations.push(a);
    map.set(a.asset_id, g);
  }
  return [...map.values()];
}

/** Optimistically add a truck's load to an order (the server's answer replaces it). */
export function withOptimisticLoad(order: BoardOrder, truck: BoardTruck, tonnes: number | null): BoardOrder {
  const allocation: BoardAllocation = {
    id: `pending-${Date.now()}`,
    order_id: order.id,
    trip_id: '',
    trip_reference: null,
    trip_stage: 'PLANNED',
    asset_id: truck.asset_id,
    registration: truck.registration,
    fleet_no: truck.fleet_no,
    planned_tonnes: tonnes,
    tonnes_basis: tonnes == null ? 'none' : 'truck',
    basis_loads: null,
    planned_start: null,
    allocated_at: new Date().toISOString(),
    allocated_by: 'you',
    released_at: null,
    released_by: null,
    counts: true,
  };
  const allocations = [...order.allocations, allocation];
  return { ...order, allocations, absorption: absorptionOf(order, allocations) };
}

/** Optimistically release an allocation. */
export function withoutLoad(order: BoardOrder, allocationId: string): BoardOrder {
  const allocations = order.allocations.map((a) => (a.id === allocationId ? { ...a, counts: false, released_at: new Date().toISOString() } : a));
  return { ...order, allocations, absorption: absorptionOf(order, allocations) };
}

/** The tonnes a truck would bring to an order: its average, else the order's planning figure. */
export const expectedTonnes = (truck: BoardTruck, order: BoardOrder): number | null =>
  truck.avg_tonnes != null && truck.avg_loads >= 3 ? truck.avg_tonnes : order.tonnes_per_load;
